import { logError } from '@hotjar/insights-common/lib/logger';
import type { FilterType, FunnelFilter } from '../../domains/global-filters/model/FilterChain';
import { FilterKindEnum } from '../../domains/global-filters/model/FilterChain';
import type { LabelProps } from './LabelFormatterType';
import { getLabels as clickedGetLabels } from './SingleLabelFormatter/ClickedLabelFormatter';
import { getLabels as newReturningUserGetLabels } from './SingleLabelFormatter/NewReturningUserLabelFormatter';
import { getLabels as rageClickGetLabels } from './SingleLabelFormatter/RageClickLabelFormatter';
import { getLabels as uTurnGetLabels } from './SingleLabelFormatter/UTurnLabelFormatter';
import { getLabels as teamCommentGetLabels } from './SingleLabelFormatter/TeamCommentLabelFormatter';
import { getLabels as deviceTypeGetLabels } from './SingleLabelFormatter/DeviceTypeLabelFormatter';
import { getLabels as operatingSystemGetLabels } from './SingleLabelFormatter/OperatingSystemLabelFormatter';
import { getLabels as countryGetLabels } from './SingleLabelFormatter/CountryLabelFormatter';
import { getLabels as eventGetLabels } from './SingleLabelFormatter/EventLabelFormatter';
import { getLabels as experimentGetLabels } from './SingleLabelFormatter/ExperimentLabelFormatter';
import { getLabels as urlGetLabels } from './SingleLabelFormatter/URLLabelFormatter';
import { getLabels as browserGetLabels } from './SingleLabelFormatter/BrowserTypeLabelFormatter';
import { getLabels as feedbackGetLabels } from './SingleLabelFormatter/FeedbackTypeLabelFormatter';
import { getLabels as surveyCompletionGetLabels } from './SingleLabelFormatter/SurveyCompletionLabelFormatter';
import { getLabels as npsGetLabels } from './SingleLabelFormatter/NPSLabelFormatter';
import { getLabels as userIdGetLabels } from './SingleLabelFormatter/UserIdLabelFormatter';
import { getLabels as keywordGetLabels } from './SingleLabelFormatter/KeywordLabelFormatter';
import { getLabels as pageCountGetLabels } from './SingleLabelFormatter/PageCountLabelFormatter';
import { getLabels as sessionDurationGetLabels } from './SingleLabelFormatter/SessionDurationLabelFormatter';
import { getLabels as userAttributesGetLabels } from './SingleLabelFormatter/UserAttributeLabelFormatter';
import { getLabels as errorGetLabels } from './SingleLabelFormatter/ErrorLabelFormatter';

const getUnknownLabel = (never: never | FunnelFilter): LabelProps => {
  logError(new Error(`Unknown Single Filter kind (We shouldn't reach this part): ${never}`));
  return {
    label1: 'Unknown',
    label2: '',
  };
};

const getLabels = (currentValue: FilterType): LabelProps => {
  switch (currentValue.kind) {
    case FilterKindEnum.NewReturningUserKind: {
      return newReturningUserGetLabels(currentValue.value);
    }
    case FilterKindEnum.RageClickKind: {
      return rageClickGetLabels(currentValue.value);
    }
    case FilterKindEnum.UTurnKind: {
      return uTurnGetLabels(currentValue.value);
    }
    case FilterKindEnum.TeamComment: {
      return teamCommentGetLabels(currentValue.value);
    }
    case FilterKindEnum.DeviceType: {
      return deviceTypeGetLabels(currentValue.value);
    }
    case FilterKindEnum.OS: {
      return operatingSystemGetLabels(currentValue.value);
    }
    case FilterKindEnum.Country: {
      return countryGetLabels(currentValue.value);
    }
    case FilterKindEnum.Event: {
      return eventGetLabels(currentValue.value);
    }
    case FilterKindEnum.GoogleOptimize: {
      return experimentGetLabels(currentValue.value, currentValue.kind);
    }
    case FilterKindEnum.Optimizely: {
      return experimentGetLabels(currentValue.value, currentValue.kind);
    }
    case FilterKindEnum.Unbounce: {
      return experimentGetLabels(currentValue.value, currentValue.kind);
    }
    case FilterKindEnum.VisitedURL: {
      return urlGetLabels(currentValue.value, currentValue.kind);
    }
    case FilterKindEnum.LandingURL: {
      return urlGetLabels(currentValue.value, currentValue.kind);
    }
    case FilterKindEnum.ExitURL: {
      return urlGetLabels(currentValue.value, currentValue.kind);
    }
    case FilterKindEnum.ReferrerURL: {
      return urlGetLabels(currentValue.value, currentValue.kind);
    }
    case FilterKindEnum.PageURL: {
      return urlGetLabels(currentValue.value, currentValue.kind);
    }
    case FilterKindEnum.Browser: {
      return browserGetLabels(currentValue.value);
    }
    case FilterKindEnum.IncomingFeedback: {
      return feedbackGetLabels(currentValue.value);
    }
    case FilterKindEnum.SurveyCompletionKind: {
      return surveyCompletionGetLabels(currentValue.value);
    }
    case FilterKindEnum.NPS: {
      return npsGetLabels(currentValue.value);
    }
    case FilterKindEnum.UserAttribute: {
      return userAttributesGetLabels(currentValue.value);
    }
    case FilterKindEnum.UserID: {
      return userIdGetLabels(currentValue.value);
    }
    case FilterKindEnum.Keyword: {
      return keywordGetLabels(currentValue.value);
    }
    case FilterKindEnum.PageCount: {
      return pageCountGetLabels(currentValue.value);
    }
    case FilterKindEnum.SessionDuration: {
      return sessionDurationGetLabels(currentValue.value);
    }
    case FilterKindEnum.Clicked: {
      return clickedGetLabels(currentValue.value);
    }
    case FilterKindEnum.Error: {
      return errorGetLabels(currentValue.value);
    }
    default:
      return getUnknownLabel(currentValue);
  }
};

export const getJoinLabel = (filter: FilterType): string => {
  const { label1, label2 } = getLabels(filter);
  if (!label1 || !label2) {
    return label1 || label2;
  }
  return `${label1} ${label2}`;
};

export const getGlobalFiltersLabel = (filterList: FilterType[]): string => {
  return filterList.map((item) => getJoinLabel(item)).join(', ');
};
